import { createServerFn } from "@tanstack/react-start";
import { fetchNotificationAlbums } from "@/lib/feed-albums.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type NotificationRow = {
  id: string;
  type:
    | "like"
    | "comment"
    | "follow"
    | "wishlist_for_sale"
    | "album_comment"
    | "album_comment_reply"
    | "comment_reply"
    | "community_like"
    | "community_comment"
    | "community_comment_reply"
    | "group_like"
    | "group_comment"
    | "group_comment_reply"
    | "message";
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
  actor: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  album: {
    id: string;
    title: string | null;
    artist: string | null;
    cover_url: string | null;
    discogs_id: string | null;
  } | null;
};


export const listNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<NotificationRow[]> => {
    const { data, error } = await context.supabase
      .from("notifications")
      .select("id, type, title, body, link, read_at, created_at, actor_id, album_id")
      .eq("recipient_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Array<{
      id: string;
      type: NotificationRow["type"];
      title: string;
      body: string | null;
      link: string | null;
      read_at: string | null;
      created_at: string;
      actor_id: string | null;
      album_id: string | null;
    }>;
    const actorIds = [...new Set(rows.map((r) => r.actor_id).filter(Boolean))] as string[];
    const albumIds = [...new Set(rows.map((r) => r.album_id).filter(Boolean))] as string[];
    let actorMap = new Map<
      string,
      { id: string; username: string; display_name: string | null; avatar_url: string | null }
    >();
    const albumMap = new Map<string, NonNullable<NotificationRow["album"]>>();
    const [, ] = await Promise.all([
      (async () => {
        if (!actorIds.length) return;
        const { data: profiles } = await context.supabase
          .from("profiles")
          .select("id, username, display_name, avatar_url")
          .in("id", actorIds);
        actorMap = new Map((profiles ?? []).map((p) => [p.id, p]));
      })(),
      (async () => {
        if (!albumIds.length) return;
        const { data: albums } = await fetchNotificationAlbums(albumIds);
        for (const a of albums ?? []) {
          albumMap.set(a.id, {
            id: a.id,
            title: a.title ?? null,
            artist: a.artist ?? null,
            cover_url: a.cover_url ?? null,
            discogs_id: a.discogs_id == null ? null : String(a.discogs_id),
          });
        }
      })(),
    ]);
    return rows.map((r) => ({
      id: r.id,
      type: r.type,
      title: r.title,
      body: r.body,
      link: r.link,
      read_at: r.read_at,
      created_at: r.created_at,
      actor: r.actor_id ? actorMap.get(r.actor_id) ?? null : null,
      album: r.album_id ? albumMap.get(r.album_id) ?? null : null,
    }));
  });


export const getUnreadNotificationCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ count: number }> => {
    const { count, error } = await context.supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("recipient_id", context.userId)
      .is("read_at", null);
    if (error) throw new Error(error.message);
    return { count: count ?? 0 };
  });

export const markNotificationRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { id: string }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("recipient_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const markAllNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("recipient_id", context.userId)
      .is("read_at", null);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const clearAllNotifications = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .delete()
      .eq("recipient_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteNotification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { id: string }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .delete()
      .eq("id", data.id)
      .eq("recipient_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
